import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users, UserPlus, Mail, Shield, Trash2, Check, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function TeamPage({ user }) {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("reviewer");
  const [sending, setSending] = useState(false);

  const isAdmin = user?.role === "admin";

  const fetchData = async () => {
    try {
      const [usersResp, invitesResp] = await Promise.all([
        api.get("/users"),
        isAdmin ? api.get("/invites") : Promise.resolve({ data: [] }),
      ]);
      setUsers(usersResp.data);
      setInvites(invitesResp.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error("Enter an email address");
      return;
    }
    setSending(true);
    try {
      await api.post("/invites", { email: inviteEmail.trim(), role: inviteRole });
      toast.success(`Invited ${inviteEmail}`);
      setInviteEmail("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
    } finally {
      setSending(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await api.delete(`/invites/${inviteId}`);
      toast.success("Invite revoked");
      fetchData();
    } catch (e) {
      toast.error("Failed to revoke invite");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      toast.success("Role updated");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update role");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:p-6 md:p-8 lg:p-12 space-y-6 lg:space-y-8 animate-fade-in" data-testid="team-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white uppercase">Team</h1>
        <p className="text-zinc-500 mt-1">Manage team members and invitations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
        {/* Invite Form - Admin only */}
        {isAdmin && (
          <Card className="bg-zinc-900/50 border-white/10 lg:col-span-1">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold text-white uppercase tracking-tight flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-400" /> Invite
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInvite} className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Email</label>
                  <Input
                    data-testid="invite-email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="name@emergent.sh"
                    className="bg-zinc-800 border-white/10 text-zinc-200"
                    type="email"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Role</label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger className="bg-zinc-800 border-white/10 text-zinc-300" data-testid="invite-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-white/10">
                      <SelectItem value="reviewer">Reviewer</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  data-testid="send-invite-btn"
                  type="submit"
                  disabled={sending}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Invite
                </Button>
              </form>

              {/* Pending invites */}
              {invites.filter(i => i.status === "pending").length > 0 && (
                <div className="mt-6 space-y-2">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Pending Invites</p>
                  {invites.filter(i => i.status === "pending").map((inv) => (
                    <div key={inv.invite_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-white/5" data-testid={`invite-${inv.invite_id}`}>
                      <div>
                        <p className="text-sm text-zinc-300">{inv.email}</p>
                        <p className="text-xs text-zinc-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {inv.role}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevokeInvite(inv.invite_id)}
                        className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Team Members */}
        <Card className={`bg-zinc-900/50 border-white/10 ${isAdmin ? "lg:col-span-2" : "lg:col-span-3"}`}>
          <CardHeader>
            <CardTitle className="font-heading text-lg font-semibold text-white uppercase tracking-tight flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-400" /> Team Members ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="team-list">
              {users.map((member) => (
                <div key={member.user_id} className="flex items-center justify-between p-4 rounded-xl bg-zinc-800/50 border border-white/5" data-testid={`team-member-${member.user_id}`}>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={member.picture} alt={member.name} />
                      <AvatarFallback className="bg-zinc-700 text-sm">{member.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-zinc-200">{member.name}</p>
                      <p className="text-xs text-zinc-500">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {member.role === "admin" ? (
                      <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/20 text-xs">
                        <Shield className="w-3 h-3 mr-1" /> Admin
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">Reviewer</Badge>
                    )}
                    {isAdmin && member.email !== user.email && (
                      <Select value={member.role} onValueChange={(val) => handleChangeRole(member.user_id, val)}>
                        <SelectTrigger className="w-28 h-7 text-xs bg-zinc-800 border-white/10 text-zinc-400">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-white/10">
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="reviewer">Reviewer</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
